import React, { useEffect, useState } from 'react';
import { Image, Popconfirm, Button, Tag, Tooltip } from 'antd';
import { DeleteOutlined, CloseOutlined } from '@ant-design/icons';

import { fixUrl } from '../services/api';
import { portadaDe } from '../utils/portadaAnimal';
import PresentacionImagen from './PresentacionImagen';

// La fila de UNA foto: miniatura a la izquierda, lo demás a la derecha. La
// misma caja para las fotos guardadas y para las que todavía no se subieron,
// así en el alta y en la edición el productor ve la misma lista.
const estiloFila = {
  display: 'flex', alignItems: 'center', gap: 16,
  padding: 12, border: '1px solid #f0f0f0', borderRadius: 8,
};
const estiloLista = { display: 'flex', flexDirection: 'column', gap: 12 };
const estiloThumb = {
  width: 72, height: 72, objectFit: 'cover', borderRadius: 6, flex: 'none',
};

/**
 * Reemplaza en `lista` la foto que volvió del backend después de un cambio de
 * presentación, y refleja el efecto lateral que ese cambio tuvo en las demás.
 *
 * Marcar una portada DESMARCA la anterior del mismo animal: lo hace el backend
 * en la misma transacción, porque la base sólo admite una. Acá se refleja, o el
 * productor ve dos marcadas donde la base tiene una. Vive acá y no en cada
 * pantalla porque la ficha del animal y el modal del catálogo lo necesitan
 * igual, y dos copias de esta regla es la forma de que un día difieran.
 */
export const conImagenActualizada = (lista, actualizada) => lista.map((img) => {
  if (img.id_imagen === actualizada.id_imagen) return actualizada;
  if (actualizada.es_portada && img.es_portada) return { ...img, es_portada: 0 };
  return img;
});

/**
 * Las fotos de UN animal, una fila por foto: la miniatura (que se abre en
 * grande al clickear), sus controles de presentación y el tachito.
 *
 * Es UNA lista y no "la foto principal": desde que un animal puede tener varias
 * fotos cargadas desde su ficha, mostrar una sola con un tachito al lado es
 * peor que no mostrar nada — el productor borra "la" foto, que es la portada,
 * y la siguiente pasa a serlo en silencio sin que haya visto las otras.
 *
 * - `onCambio(actualizada)`: la foto como volvió del backend tras tocar un
 *   control. El que llama la mete en su lista con `conImagenActualizada`.
 * - `onEliminar(imagen)`: si no se pasa, no hay tachito. La ficha lo pasa; el
 *   modal del catálogo no, porque ese modal es de presentación.
 * - `conPresentacion`: los tres controles escriben con `PUT /imagenes/:id`,
 *   que va con `fotos_videos`, y sólo tienen sentido para quien publica un
 *   catálogo de genética. Sin ellos, la fila igual dice cuál es la portada —la
 *   marcada, o la primera— con una etiqueta, porque es la que sale en las
 *   miniaturas del panel.
 */
const FotosAnimal = ({
  imagenes, onCambio, onEliminar, conPresentacion = true, eliminando = null,
}) => {
  const portada = conPresentacion ? null : portadaDe(imagenes);
  return (
    <div style={estiloLista}>
      {imagenes.map((img) => (
        <div key={img.id_imagen} style={estiloFila}>
          <Image
            src={fixUrl(img.url_thumb || img.url_imagen)}
            // La miniatura es de 400px; en grande se abre el archivo original.
            preview={{ src: fixUrl(img.url_imagen) }}
            alt=""
            width={72}
            height={72}
            style={{
              ...estiloThumb,
              // Se muestra con el encuadre elegido: sirve de confirmación de lo
              // que se acaba de tocar, sin ir a otra pantalla.
              objectPosition: `${img.foco_x ?? 50}% ${img.foco_y ?? 50}%`,
            }}
          />
          <div style={{ flex: 'auto', minWidth: 0 }}>
            {conPresentacion
              ? <PresentacionImagen imagen={img} onCambio={onCambio} />
              : (portada && portada.id_imagen === img.id_imagen && <Tag color="blue">Portada</Tag>)}
          </div>
          {onEliminar && (
            <Popconfirm
              title="¿Eliminar esta foto?"
              description="Se borra el archivo. No se puede deshacer."
              onConfirm={() => onEliminar(img)}
              okText="Sí"
              cancelText="No"
            >
              <Tooltip title="Eliminar">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  loading={eliminando === img.id_imagen}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      ))}
    </div>
  );
};

/**
 * Las fotos elegidas en el ALTA, antes de que exista el animal: no tienen id
 * ni URL, son `File` del input. Se listan igual que las guardadas, con una cruz
 * para sacar una de la tanda —sin confirmación, porque todavía no se subió
 * nada y no hay nada que perder—.
 *
 * LOS BLOBS SE CREAN Y SE REVOCAN ACÁ. El navegador retiene cada
 * `URL.createObjectURL` hasta que se cierra la pestaña, así que elegir cinco
 * fotos y sacarlas dejaba archivos completos colgados en memoria. Un efecto
 * por lista: crea las URLs de la lista actual y las revoca cuando cambia o
 * cuando el componente se va.
 */
export const FotosPendientes = ({ archivos, onQuitar }) => {
  const [urls, setUrls] = useState([]);
  useEffect(() => {
    const creadas = archivos.map((f) => URL.createObjectURL(f));
    setUrls(creadas);
    return () => { creadas.forEach((u) => URL.revokeObjectURL(u)); };
  }, [archivos]);

  return (
    <div style={estiloLista}>
      {archivos.map((archivo, i) => (
        // El índice como key y no el nombre: dos archivos pueden llamarse
        // igual, y la lista sólo cambia de a tandas enteras.
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} style={estiloFila}>
          {urls[i] && <img src={urls[i]} alt="" style={estiloThumb} />}
          <div style={{ flex: 'auto', minWidth: 0, fontSize: 12, color: '#888' }}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {archivo.name}
            </div>
            <div>Se sube al crear el animal.</div>
          </div>
          <Tooltip title="Sacar de la tanda">
            <Button type="text" icon={<CloseOutlined />} onClick={() => onQuitar(i)} />
          </Tooltip>
        </div>
      ))}
    </div>
  );
};

export default FotosAnimal;
